/**
 * INTRO OVERLAY (B6): the words of the intro and its skip, over the map's own beats (spec §E.7).
 *
 *   <IntroOverlay choreo={choreo} mode={introSeen || reduced ? "short" : "full"} onDone={(skipped) => …} />
 *
 * Mount it when the visitor enters (LoaderScreen onEnter), while the loader still covers the map. It
 * waits for `choreo`, then runs the intro once (IntroRunner): phase → "intro", choreo.intro({ mode,
 * signal }), the two brand lines keyed off the intro:beat events, "Skip the introduction" from 600 ms
 * until the ui beat, phase → "explore" at the ui beat, and at the end markIntroSeen(), bus
 * "intro:done" {skipped} and onDone(skipped). After that it renders nothing; the parent may unmount it.
 *
 * Short intro (return visit, reduced motion) and skip: the map jumps to its final state while a night
 * veil lifts over 420 drift, and any words leave on the same 420 cross-fade.
 *
 * Accessibility: the words sit in an aria-live="polite" region (text is inserted as each line
 * arrives); the overlay never takes focus and never traps it; only the skip button takes pointers.
 * Escape skips while the button is offered (Enter/Space on the button, natively). If the button
 * had focus when it leaves, focus moves to the map region (§H.1).
 *
 * StrictMode: the run survives the simulated unmount/remount (it is only cancelled if the component
 * is really gone on the next task), so the map's intro is never aborted and restarted.
 */
import { useEffect, useRef, useState, type JSX } from "react";
import type { Choreographer } from "@/contracts/map";
import type { IntroOverlayProps } from "@/contracts/ui";
import { Button, cx } from "@/design";
import { useApp } from "@/store/store";
import { INTRO_COPY, type IntroView } from "./introModel";
import { IntroRunner } from "./introRunner";
import "./intro.css";

export { skipIntro } from "./introRunner";

interface Held {
  runner: IntroRunner;
  choreo: Choreographer;
  timer: number | undefined;
}

/** Where focus goes when the skip button leaves while focused (§H.1: the map region). */
function focusMapRegion(): void {
  const target =
    document.querySelector<HTMLElement>('[data-ld-slot="map"]') ??
    document.querySelector<HTMLElement>(".maplibregl-canvas") ??
    document.querySelector<HTMLElement>('[aria-roledescription="map"]');
  if (target) {
    if (!target.hasAttribute("tabindex") && target.tabIndex < 0) target.setAttribute("tabindex", "-1");
    target.focus({ preventScroll: true });
  } else if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
}

/** The veil lifts: night at .55 now, then the CSS transition carries it to 0 (420 drift). */
function dip(el: HTMLElement): void {
  el.style.transition = "none";
  el.style.opacity = "0.55";
  void el.offsetWidth; // commit the start value before the transition takes over
  el.style.transition = "";
  el.style.opacity = "";
}

export function IntroOverlay({ choreo, mode, onDone }: IntroOverlayProps): JSX.Element | null {
  const layout = useApp((s) => s.layout);
  const mood = useApp((s) => s.mood);
  const [view, setView] = useState<IntroView | null>(null);
  const held = useRef<Held | null>(null);
  const scrimRef = useRef<HTMLDivElement>(null);
  const skipRef = useRef<HTMLDivElement>(null);
  const onDoneRef = useRef(onDone);
  const modeRef = useRef(mode);
  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  /* One run per choreographer. */
  useEffect(() => {
    if (!choreo) return undefined;
    let h = held.current;
    if (h && h.choreo === choreo) {
      window.clearTimeout(h.timer); // StrictMode remount: keep the run going
      h.timer = undefined;
    } else {
      h?.runner.cancel({ settle: false }); // the map was replaced: start over on the new one
      const runner = new IntroRunner({ choreo, mode: modeRef.current, onView: setView, onDone: (skipped) => onDoneRef.current(skipped) });
      h = held.current = { runner, choreo, timer: undefined };
      setView(runner.view);
      runner.start();
    }
    const mine = h;
    return () => {
      mine.timer = window.setTimeout(() => {
        if (held.current !== mine) return;
        held.current = null;
        mine.runner.cancel({ settle: true });
      }, 0);
    };
  }, [choreo]);

  const skipShown = view?.skip === "shown";
  const skipGone = view?.skip === "gone";
  const dipKey = !view || !view.started ? "" : view.mode === "short" ? "short" : view.skipped ? "skip" : "";

  /* Escape skips while the button is offered. document (bubble) runs before B10's window Escape. */
  useEffect(() => {
    if (!skipShown) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || e.isComposing) return;
      if (held.current?.runner.skip()) e.preventDefault();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [skipShown]);

  /* The button is leaving: never strand focus on it. */
  useEffect(() => {
    if (!skipGone) return;
    const wrap = skipRef.current;
    if (wrap && document.activeElement && wrap.contains(document.activeElement)) focusMapRegion();
  }, [skipGone]);

  /* Short intro and skip: the veil lifts over the cut. */
  useEffect(() => {
    if (dipKey && scrimRef.current) dip(scrimRef.current);
  }, [dipKey]);

  if (!view || view.done) return null;
  // A local dusk behind the words from line 1 until line 2 leaves: the sunrise brightens the land under them.
  const shadeOn = view.mode === "full" && view.line1 !== "idle" && view.line2 !== "gone" && !view.skipped && !view.ui;
  const lineClass = (s: IntroView["line1"]) => cx("ld-hero ld-loc-intro-line", s === "on" && "is-on", s === "gone" && "is-gone", view.fast && "ld-loc-intro-line--fast");

  return (
    // data-land: the light the land is in under the skip (night until the sunrise, then the mood it rose to).
    <div className="ld-loc-intro" data-layout={layout} data-mode={view.mode} data-land={view.sunrise ? mood : "night"}>
      <div ref={scrimRef} className="ld-loc-intro-scrim" aria-hidden="true" />
      {view.mode === "full" && <div className={cx("ld-loc-intro-shade", shadeOn && "is-on", view.fast && "is-fast")} aria-hidden="true" />}
      {view.mode === "full" && (
        <div className="ld-intro ld-loc-intro-lines" aria-live="polite">
          <p className={lineClass(view.line1)}>{view.line1 === "idle" ? "" : INTRO_COPY.line1}</p>
          <p className={lineClass(view.line2)}>{view.line2 === "idle" ? "" : INTRO_COPY.line2}</p>
        </div>
      )}
      {view.skip !== "hidden" && (
        <div ref={skipRef} className={cx("ld-skip ld-dark ld-loc-intro-skip", skipGone && "is-gone")} aria-hidden={skipGone || undefined}>
          <Button variant="quiet" className="ld-loc-intro-skip-btn" tabIndex={skipGone ? -1 : undefined} onClick={() => held.current?.runner.skip()}>
            {INTRO_COPY.skip}
          </Button>
        </div>
      )}
    </div>
  );
}
